'use client';

import { c as _c } from "react/compiler-runtime";
import { createContext, use, useEffect, useState } from 'react';
import { ToggleGroup as ToggleGroupPrimitive } from 'radix-ui';
import { tv } from 'tailwind-variants';
import { toggle } from './Toggle';
import type { VariantProps } from 'tailwind-variants';
type ToggleGroupContextValue = VariantProps<typeof toggleGroup>;
const ToggleGroupContext = createContext<ToggleGroupContextValue | null>(null);
const toggleGroup = tv({
  base: `
    group/toggle-group
    flex
    flex-wrap
    w-fit
    items-center
    rounded-md
  `,
  variants: {
    variant: {
      bounded: 'border-border-secondary border',
      unbounded: 'gap-2',
      card: 'gap-4 grow w-auto',
      cardHorizontal: 'gap-2 grow w-auto'
    },
    size: {
      sm: '',
      md: '',
      lg: ''
    }
  },
  defaultVariants: {
    variant: 'bounded',
    size: 'md'
  }
});
type ToggleGroupProps = React.ComponentProps<typeof ToggleGroupPrimitive.Root> & VariantProps<typeof toggleGroup>;
function ToggleGroup(t0) {
  const $ = _c(32);
  let children;
  let className;
  let defaultValue;
  let onValueChange;
  let props;
  let size;
  let valueProp;
  let variant;
  if ($[0] !== t0) {
    ({
      className,
      variant,
      size,
      children,
      value: valueProp,
      defaultValue,
      onValueChange,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = defaultValue;
    $[4] = onValueChange;
    $[5] = props;
    $[6] = size;
    $[7] = valueProp;
    $[8] = variant;
  } else {
    children = $[1];
    className = $[2];
    defaultValue = $[3];
    onValueChange = $[4];
    props = $[5];
    size = $[6];
    valueProp = $[7];
    variant = $[8];
  }
  const [value, setValue] = useState(valueProp ?? defaultValue);
  let t1;
  if ($[9] !== defaultValue || $[10] !== onValueChange || $[11] !== valueProp) {
    t1 = () => {
      if (valueProp === undefined && defaultValue !== undefined && onValueChange) {
        onValueChange(defaultValue as string & string[]);
      }
    };
    $[9] = defaultValue;
    $[10] = onValueChange;
    $[11] = valueProp;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  let t2;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  useEffect(t1, t2);
  let t3;
  if ($[14] !== className || $[15] !== size || $[16] !== variant) {
    t3 = toggleGroup({
      variant,
      size,
      className
    });
    $[14] = className;
    $[15] = size;
    $[16] = variant;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  let t4;
  if ($[18] !== onValueChange) {
    t4 = newValue => {
      const isDeselect = newValue === null || newValue === undefined || newValue === "" || Array.isArray(newValue) && newValue.length === 0;
      if (!isDeselect) {
        setValue(newValue);
        onValueChange?.(newValue as string & string[]);
      }
    };
    $[18] = onValueChange;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  const t5 = valueProp ?? value;
  let t6;
  if ($[20] !== size || $[21] !== variant) {
    t6 = {
      variant,
      size
    };
    $[20] = size;
    $[21] = variant;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== children || $[24] !== t6) {
    t7 = <ToggleGroupContext.Provider value={t6}>{children}</ToggleGroupContext.Provider>;
    $[23] = children;
    $[24] = t6;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== props || $[27] !== t3 || $[28] !== t4 || $[29] !== t5 || $[30] !== t7) {
    t8 = <ToggleGroupPrimitive.Root className={t3} data-slot="toggle-group" onValueChange={t4} value={t5} {...props}>{t7}</ToggleGroupPrimitive.Root>;
    $[26] = props;
    $[27] = t3;
    $[28] = t4;
    $[29] = t5;
    $[30] = t7;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  return t8;
}
const toggleGroupItem = tv({
  extend: toggle,
  // Inherit base styles and variants from toggle
  base: `
    focus:z-10
    focus-visible:z-10
    font-medium!
    shrink-0
  `,
  variants: {
    variant: {
      bounded: `
        rounded-none!
        first-of-type:rounded-l-sm!
        last-of-type:rounded-r-sm!
        data-[state=on]:rounded-sm!
      `,
      card: `
        rounded-sm!
        p-4
        flex-1
        min-w-[150px]!
        h-[150px]!
        flex
        grow
        bg-background-primary
        border-border-secondary
        data-[state=on]:bg-background-secondary
        data-[state=on]:border-background-accent
      `,
      cardHorizontal: `
        rounded-sm!
        p-4
        flex-1
        h-auto
        min-w-[292px]!
        flex
        flex-row
        items-center
        bg-background-primary
        border-border-secondary
        data-[state=on]:bg-background-secondary
        data-[state=on]:border-background-accent
      `
    }
  }
});
type ToggleGroupItemProps = React.ComponentProps<typeof ToggleGroupPrimitive.Item> & VariantProps<typeof toggleGroupItem>;
function ToggleGroupItem(t0) {
  const $ = _c(14);
  let children;
  let className;
  let props;
  let size;
  let variant;
  if ($[0] !== t0) {
    ({
      className,
      children,
      variant,
      size,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = size;
    $[5] = variant;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    size = $[4];
    variant = $[5];
  }
  const context = use(ToggleGroupContext);
  const finalVariant = context?.variant ?? variant;
  const finalSize = context?.size ?? size;
  let t1;
  if ($[6] !== className || $[7] !== finalSize || $[8] !== finalVariant) {
    t1 = toggleGroupItem({
      variant: finalVariant,
      size: finalSize,
      className
    });
    $[6] = className;
    $[7] = finalSize;
    $[8] = finalVariant;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[10] !== children || $[11] !== props || $[12] !== t1) {
    t2 = <ToggleGroupPrimitive.Item className={t1} data-slot="toggle-group-item" {...props}>{children}</ToggleGroupPrimitive.Item>;
    $[10] = children;
    $[11] = props;
    $[12] = t1;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  return t2;
}
export { ToggleGroup, ToggleGroupItem, toggleGroup, toggleGroupItem, type ToggleGroupProps, type ToggleGroupItemProps };